// MembershipDetail.jsx - Detailed view for a single client membership
// Shows: start date, plan info, treatments received, billing history, next charge, action buttons

import { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { fmtAppointmentDate } from "./dateUtils";
import { fnErrorMessage } from "./fnError";

// Fallback wording when the server didn't send its own message.
const ACTION_DONE = {
  charge_now: "Charged successfully.",
  cancel_subscription: "Membership cancelled — no more monthly charges.",
  pause_subscription: "Membership paused.",
  resume_subscription: "Membership resumed — billing continues.",
};

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

export default function MembershipDetail({ membershipId, onBack }) {
  const [m, setM] = useState(null);
  const [charges, setCharges] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // success confirmation panel

  useEffect(() => { load(); }, [membershipId]);

  const load = async () => {
    setLoading(true);
    const { data: mData } = await supabase.from("client_memberships")
      .select("*, clients(*), membership_plans(*)")
      .eq("id", membershipId).single();
    setM(mData);

    if (mData) {
      const { data: ch } = await supabase.from("membership_charges")
        .select("*").eq("membership_id", membershipId)
        .order("charged_at", { ascending: false });
      setCharges(ch || []);

      // Find treatments received since membership started
      const { data: ap } = await supabase.from("appointments")
        .select("*").eq("cid", mData.client_id)
        .gte("date", mData.started_at?.split("T")[0])
        .order("date", { ascending: false });
      setAppointments(ap || []);
    }
    setLoading(false);
  };

  const action = async (act, extra = {}) => {
    setActionLoading(act);
    setError("");
    setResult(null);
    try {
      const { data, error: e } = await supabase.functions.invoke("stripe-membership", {
        body: { action: act, membership_id: membershipId, ...extra },
      });
      // Surface the server's real explanation, not "non-2xx status code".
      if (e || data?.error) {
        const msg = await fnErrorMessage(e, data, `${act} failed`);
        console.error("[membership]", act, "→", msg, e);
        throw new Error(msg);
      }
      // ★ Confirm out loud. Charging a card with no visible outcome left staff
      //   unsure whether the client had actually been billed (owner report
      //   2026-09-02 after Maria's $444 went through silently).
      setResult({ action: act, message: data?.message || ACTION_DONE[act] || "Done." });
      await load();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setActionLoading("");
    }
  };

  if (loading) return <div style={{padding:60,textAlign:"center",color:C.muted}}>Loading...</div>;
  if (!m) return <div style={{padding:60,textAlign:"center",color:C.red}}>Membership not found</div>;

  const totalPaid = charges.filter(c=>c.status==="succeeded").reduce((s,c)=>s+ +c.amount, 0);
  const monthsActive = Math.max(1, Math.floor((Date.now() - new Date(m.started_at).getTime()) / (1000*60*60*24*30)));
  const isActive = m.status === "active";
  const isPaused = m.status === "paused";
  const isCancelled = m.status === "cancelled";

  return (
    <div>
      <button onClick={onBack} style={{padding:"7px 14px",background:C.light,border:"1px solid "+C.border,borderRadius:7,fontSize:12,color:C.muted,cursor:"pointer",fontFamily:F,marginBottom:14}}>
        ← Back to memberships
      </button>

      {error && <div style={{padding:"10px 14px",background:C.redL,color:C.red,border:"1px solid "+C.red+"40",borderRadius:7,fontSize:12,marginBottom:14}}>❌ {error}</div>}

      {/* ★ Success confirmation — a card charge must never complete silently. */}
      {result && (
        <div style={{position:"fixed",inset:0,zIndex:4500,background:"rgba(15,15,25,0.6)",backdropFilter:"blur(3px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
             onClick={()=>setResult(null)}>
          <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:16,maxWidth:420,width:"100%",padding:26,textAlign:"center",boxShadow:"0 24px 70px rgba(0,0,0,0.4)",border:"3px solid #2e8b57"}}>
            <div style={{fontSize:46,lineHeight:1,marginBottom:10}}>{result.action==="charge_now"?"💳✅":"✅"}</div>
            <div style={{fontSize:19,fontWeight:800,color:"#1a1a2e",fontFamily:S,marginBottom:8}}>
              {result.action==="charge_now" ? "Payment Successful" : "Done"}
            </div>
            <div style={{fontSize:13.5,color:"#55524a",lineHeight:1.6,marginBottom:6}}>{result.message}</div>
            {m?.clients?.name && (
              <div style={{fontSize:12,color:"#8a8276"}}>{m.clients.name} · {m.membership_plans?.name}</div>
            )}
            <button onClick={()=>setResult(null)} style={{marginTop:18,width:"100%",padding:"12px",fontSize:14,fontWeight:700,background:"linear-gradient(135deg,#2e8b57,#246b45)",color:"#fff",border:"none",borderRadius:10,cursor:"pointer",fontFamily:F}}>Close</button>
          </div>
        </div>
      )}

      {/* Header card */}
      <div style={{
        background:"linear-gradient(135deg,"+(m.membership_plans?.color||C.gold)+",rgba(180,140,60,0.7))",
        color:"#fff",
        borderRadius:14,
        padding:"22px 26px",
        marginBottom:18,
      }}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:14,flexWrap:"wrap"}}>
          <div>
            <div style={{fontSize:11,fontWeight:600,opacity:0.85,textTransform:"uppercase",letterSpacing:"0.1em"}}>{statusLabel(m.status)} Member</div>
            <div style={{fontSize:28,fontWeight:800,fontFamily:S,marginTop:4}}>{m.clients?.name}</div>
            <div style={{fontSize:13,opacity:0.85}}>{m.clients?.email} · {m.clients?.phone}</div>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:11,fontWeight:600,opacity:0.85,textTransform:"uppercase",letterSpacing:"0.1em"}}>Plan</div>
            <div style={{fontSize:20,fontWeight:800,fontFamily:S,marginTop:4}}>{m.membership_plans?.name}</div>
            <div style={{fontSize:13,opacity:0.85}}>${(+m.membership_plans?.price||0).toFixed(0)}/{m.membership_plans?.billing_interval}</div>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12,marginBottom:18}}>
        <Stat label="Status" value={statusLabel(m.status)} color={statusColor(m.status)}/>
        <Stat label="Member Since" value={new Date(m.started_at).toLocaleDateString()} color={C.gold}/>
        <Stat label="Months Active" value={monthsActive} color={C.green}/>
        <Stat label="Total Paid" value={`$${totalPaid.toFixed(2)}`} color={C.green}/>
        <Stat label="Visits" value={appointments.length} color={C.blue}/>
        <Stat label="Next Charge" value={m.next_billing_at && !isCancelled ? new Date(m.next_billing_at).toLocaleDateString() : "—"} color={isCancelled?C.muted:C.gold}/>
      </div>

      {/* Cancelling-at-period-end banner (cancel requested but not yet ended) */}
      {m.cancelled_at && !isCancelled && (
        <div style={{padding:"10px 14px",background:C.redL,color:C.red,border:"1px solid "+C.red+"40",borderRadius:8,fontSize:12,marginBottom:14,fontWeight:600}}>
          ⏳ Cancelling at period end — no further charges. {m.next_billing_at ? "Ends "+new Date(m.next_billing_at).toLocaleDateString()+"." : ""}
        </div>
      )}

      {/* Action buttons */}
      <div style={{display:"flex",gap:8,marginBottom:8,flexWrap:"wrap"}}>
        {(isActive || m.status==="trialing") && (
          <button onClick={()=>action("pause_subscription")} disabled={actionLoading==="pause_subscription"} style={btn(C.blue, false)}>
            {actionLoading==="pause_subscription"?"Pausing...":"⏸ Pause Membership"}
          </button>
        )}
        {isPaused && (
          <button onClick={()=>action("resume_subscription")} disabled={actionLoading==="resume_subscription"} style={btn(C.green, true)}>
            {actionLoading==="resume_subscription"?"Resuming...":"▶ Resume Membership"}
          </button>
        )}
        {/* Cancel is available for ANY billable subscription (active, trialing,
            paused, past due) — not just "active", which used to hide it for
            trial-anchored members. Stops all future monthly billing. */}
        {!isCancelled && m.stripe_subscription_id && (
          <button onClick={()=>{ if(window.confirm(`Cancel ${m.clients?.name||"this member"}'s membership?\n\nBilling will STOP — they won't be charged again. They keep their benefits through the period already paid for.`)) action("cancel_subscription", {immediate:false}); }} disabled={actionLoading==="cancel_subscription"} style={btn(C.red, true)}>
            {actionLoading==="cancel_subscription"?"Cancelling...":"✕ Cancel Membership"}
          </button>
        )}
        {!isCancelled && (
          <button onClick={()=>{ if(window.confirm("Charge now? This will attempt to process the latest invoice.")) action("charge_now"); }} disabled={actionLoading==="charge_now"} style={btn(C.gold, true)}>
            {actionLoading==="charge_now"?"Charging...":"💳 Charge Now"}
          </button>
        )}
      </div>
      {/* Immediate cancel for when they want billing + access to end today. */}
      {!isCancelled && m.stripe_subscription_id && (
        <div style={{marginBottom:18}}>
          <button onClick={()=>{ if(window.confirm("Cancel IMMEDIATELY?\n\nBilling stops now and the membership ends today (no remaining paid period).")) action("cancel_subscription", {immediate:true}); }} disabled={actionLoading==="cancel_subscription"} style={{background:"none",border:"none",color:C.muted,fontSize:11,textDecoration:"underline",cursor:"pointer",fontFamily:F,padding:0}}>
            or cancel immediately (end access today)
          </button>
        </div>
      )}

      {/* Two-column: Treatments + Charges */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(320px,1fr))",gap:14}}>
        {/* Treatments */}
        <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,overflow:"hidden"}}>
          <div style={{padding:"14px 16px",borderBottom:"1px solid "+C.border,background:C.goldBg}}>
            <div style={{fontSize:14,fontWeight:700,color:C.text,fontFamily:S}}>💆 Treatments Received</div>
            <div style={{fontSize:11,color:C.muted}}>{appointments.length} appointments since joining</div>
          </div>
          <div style={{maxHeight:380,overflow:"auto"}}>
            {appointments.length === 0 ? (
              <div style={{padding:30,textAlign:"center",color:C.muted,fontSize:12}}>No treatments yet</div>
            ) : appointments.map(a => (
              <div key={a.id} style={{padding:"11px 16px",borderBottom:"1px solid "+C.border}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:600,color:C.text}}>{a.treat || "—"}</div>
                    <div style={{fontSize:11,color:C.muted}}>{fmtAppointmentDate(a.date)} at {a.time} · {a.prov || "—"}</div>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0,fontSize:13,fontWeight:700,color:C.gold}}>${(+a.price||0).toFixed(0)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Billing history */}
        <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,overflow:"hidden"}}>
          <div style={{padding:"14px 16px",borderBottom:"1px solid "+C.border,background:C.goldBg}}>
            <div style={{fontSize:14,fontWeight:700,color:C.text,fontFamily:S}}>💳 Billing History</div>
            <div style={{fontSize:11,color:C.muted}}>{charges.length} charges</div>
          </div>
          <div style={{maxHeight:380,overflow:"auto"}}>
            {charges.length === 0 ? (
              <div style={{padding:30,textAlign:"center",color:C.muted,fontSize:12}}>No charges yet</div>
            ) : charges.map(c => (
              <div key={c.id} style={{padding:"11px 16px",borderBottom:"1px solid "+C.border}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:c.status==="succeeded"?C.green:c.status==="failed"?C.red:C.muted}}>${(+c.amount).toFixed(2)}</div>
                    <div style={{fontSize:11,color:C.muted}}>{new Date(c.charged_at).toLocaleDateString()} · {c.status}</div>
                    {c.failure_reason && <div style={{fontSize:10,color:C.red,marginTop:2}}>⚠ {c.failure_reason}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{padding:14,background:C.card,border:"1px solid "+C.border,borderRadius:10,borderLeft:"4px solid "+color}}>
      <div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>{label}</div>
      <div style={{fontSize:18,fontWeight:800,color:C.text,fontFamily:S}}>{value}</div>
    </div>
  );
}

function statusLabel(s) {
  return ({active:"Active", paused:"Paused", cancelled:"Cancelled", past_due:"Past Due", incomplete:"Pending"})[s] || s;
}
function statusColor(s) {
  return ({active:C.green, paused:C.blue, cancelled:C.red, past_due:"#cc6600", incomplete:C.muted})[s] || C.muted;
}

function btn(color, primary) {
  return {
    padding:"9px 16px",
    background: primary ? `linear-gradient(135deg,${color},${darken(color)})` : "#fff",
    color: primary ? "#fff" : color,
    border: "1px solid " + color,
    borderRadius: 7,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: F,
  };
}
function darken(hex) {
  const n = parseInt(hex.replace("#",""), 16);
  return "#" + Math.max(0,((n>>16)-30)).toString(16).padStart(2,"0") + Math.max(0,(((n>>8)&0xff)-30)).toString(16).padStart(2,"0") + Math.max(0,((n&0xff)-30)).toString(16).padStart(2,"0");
}
